/**
 * Per-item financial computations for the project Financials tab (D3).
 *
 * Spec §6.1 lays out the project-level totals:
 *
 *   Project contract value = Σ (part.qty × part.client_unit_price)
 *   Project cost           = Σ part costs
 *                              - parts without sub-parts: part.qty × part.supplier_unit_price
 *                              - parts with sub-parts: Σ over sub-parts of
 *                                  sub-part.total_required_qty × sub-part.supplier_unit_price
 *   Project margin         = value − cost
 *
 * The server already computes the project-level totals in derived state
 * (B1 added `marginPaisa`). For the Financials tab we also want the
 * per-part breakdown — and that breakdown has to follow the same Variant A
 * / Variant B distinction. These helpers do that, taking pre-fetched
 * items as input.
 */

import type { ProjectItem } from './pipeline-queries';


export interface ScheduleAmountComparison {
  /** Original project amount entered in General Particulars. */
  originalAmountPaisa: number | null;
  /** Current part-wise Schedule of Stores total, excluding GST. */
  scheduleOfStoresTotalPaisa: number;
  /** originalAmountPaisa − scheduleOfStoresTotalPaisa. Null until both sides are meaningfully comparable. */
  discrepancyPaisa: number | null;
  /** Absolute value of discrepancyPaisa for user-facing warning copy. */
  absoluteDiscrepancyPaisa: number | null;
  /** True when both sides can be compared and are not equal. */
  hasDiscrepancy: boolean;
  /** True when an original amount exists and at least one SOS amount has been entered. */
  canCompare: boolean;
}

/**
 * Compare the original project amount against the Schedule of Stores total.
 *
 * The original amount is the project-level total store value from General
 * Particulars. The Schedule of Stores total is the derived sum of entered
 * parts: qty × client unit price, before GST. We intentionally avoid raising
 * a discrepancy warning while no SOS value has been entered yet, otherwise a
 * newly created project would immediately warn against an empty schedule.
 */
export function computeScheduleAmountComparison(
  originalAmountPaisa: number | null | undefined,
  scheduleOfStoresTotalPaisa: number | null | undefined,
): ScheduleAmountComparison {
  const original = originalAmountPaisa ?? null;
  const sosTotal = Math.max(0, scheduleOfStoresTotalPaisa ?? 0);
  const canCompare = original !== null && sosTotal > 0;
  const discrepancyPaisa = canCompare ? original - sosTotal : null;
  const absoluteDiscrepancyPaisa = discrepancyPaisa === null ? null : Math.abs(discrepancyPaisa);

  return {
    originalAmountPaisa: original,
    scheduleOfStoresTotalPaisa: sosTotal,
    discrepancyPaisa,
    absoluteDiscrepancyPaisa,
    hasDiscrepancy: discrepancyPaisa !== null && discrepancyPaisa !== 0,
    canCompare,
  };
}

export interface PartFinancialRow {
  part: ProjectItem;
  /** part.qty × unit price before GST */
  contractValuePaisa: number;
  /** contractValuePaisa × part GST %. Null when GST % is not provided. */
  gstAmountPaisa: number | null;
  /** contractValuePaisa + gstAmountPaisa. Null when GST % is not provided. */
  totalValuePaisa: number | null;
  /**
   * For Variant B (no sub-parts): part.qty × supplier_unit_price.
   * For Variant A (with sub-parts): rolled up from sub-parts. May be 0 if
   *   any sub-part supplier price is null (defensive — the schema requires
   *   it but defence against partial data prevents NaNs in the UI).
   */
  costPaisa: number;
  /** contractValuePaisa − costPaisa. Signed. */
  marginPaisa: number;
  /** True when costPaisa was rolled up across sub-parts (Variant A). */
  isCostRolledUp: boolean;
}

export interface SubPartFinancialRow {
  subPart: ProjectItem;
  parent: ProjectItem;
  /** parent.qty × subPart.quantityPerParent */
  totalRequiredQuantity: number;
  /** totalRequiredQuantity × supplier_unit_price */
  costPaisa: number;
}

/**
 * Build per-part financial rows from the project's items.
 *
 * Returns rows in srNumber order. Sub-parts under a part are NOT in this
 * list — call `buildSubPartFinancialRows` for those.
 */
export function buildPartFinancialRows(items: readonly ProjectItem[]): PartFinancialRow[] {
  const liveItems = items.filter((i) => i.deletedAt === null);
  const parts = liveItems.filter((i) => i.itemType === 'PART');
  const subPartsByParent = new Map<string, ProjectItem[]>();
  for (const sp of liveItems.filter((i) => i.itemType === 'SUB_PART')) {
    if (!sp.parentItemId) continue;
    const arr = subPartsByParent.get(sp.parentItemId) ?? [];
    arr.push(sp);
    subPartsByParent.set(sp.parentItemId, arr);
  }

  const rows: PartFinancialRow[] = [];
  for (const part of parts) {
    const qty = part.partQuantity ?? 0;
    const clientPrice = part.clientUnitPricePaisa ?? 0;
    const contractValuePaisa = qty * clientPrice;
    const gstAmountPaisa = part.gstRateBps != null
      ? Math.round((contractValuePaisa * part.gstRateBps) / 10_000)
      : null;
    const totalValuePaisa = gstAmountPaisa != null ? contractValuePaisa + gstAmountPaisa : null;

    const children = subPartsByParent.get(part.id) ?? [];
    let costPaisa: number;
    let isCostRolledUp: boolean;
    if (children.length > 0) {
      // Variant A — roll up sub-part costs
      isCostRolledUp = true;
      costPaisa = 0;
      for (const sp of children) {
        const required = qty * (sp.quantityPerParent ?? 0);
        const supplierPrice = sp.supplierUnitPricePaisa ?? 0;
        costPaisa += required * supplierPrice;
      }
    } else {
      // Variant B — direct supplier price on the part
      isCostRolledUp = false;
      const supplierPrice = part.supplierUnitPricePaisa ?? 0;
      costPaisa = qty * supplierPrice;
    }

    rows.push({
      part,
      contractValuePaisa,
      gstAmountPaisa,
      totalValuePaisa,
      costPaisa,
      marginPaisa: contractValuePaisa - costPaisa,
      isCostRolledUp,
    });
  }

  // Sort by Sr# ascending so display order matches the Parts tab
  rows.sort((a, b) => (a.part.srNumber ?? 0) - (b.part.srNumber ?? 0));
  return rows;
}

/**
 * Build per-sub-part financial rows from the project's items.
 *
 * Returns rows sorted by parent Sr# then sub-part name.
 */
export function buildSubPartFinancialRows(items: readonly ProjectItem[]): SubPartFinancialRow[] {
  const liveItems = items.filter((i) => i.deletedAt === null);
  const partsById = new Map<string, ProjectItem>();
  for (const p of liveItems.filter((i) => i.itemType === 'PART')) {
    partsById.set(p.id, p);
  }

  const rows: SubPartFinancialRow[] = [];
  for (const sp of liveItems.filter((i) => i.itemType === 'SUB_PART')) {
    if (!sp.parentItemId) continue;
    const parent = partsById.get(sp.parentItemId);
    if (!parent) continue;
    const parentQty = parent.partQuantity ?? 0;
    const qtyPerParent = sp.quantityPerParent ?? 0;
    const totalRequiredQuantity = parentQty * qtyPerParent;
    const supplierPrice = sp.supplierUnitPricePaisa ?? 0;
    rows.push({
      subPart: sp,
      parent,
      totalRequiredQuantity,
      costPaisa: totalRequiredQuantity * supplierPrice,
    });
  }

  rows.sort((a, b) => {
    const srDelta = (a.parent.srNumber ?? 0) - (b.parent.srNumber ?? 0);
    if (srDelta !== 0) return srDelta;
    const subSrDelta = (a.subPart.srNumber ?? 0) - (b.subPart.srNumber ?? 0);
    if (subSrDelta !== 0) return subSrDelta;
    return a.subPart.name.localeCompare(b.subPart.name);
  });
  return rows;
}
